import { useCallback, useEffect, useRef, useState } from "react";
import grapesjs, { type Editor as GrapesEditor } from "grapesjs";
import presetNewsletter from "grapesjs-preset-newsletter";
import "grapesjs/dist/css/grapes.min.css";
import { tvsBlocksPlugin } from "./grapesPlugins/tvsBlocks";
import { variablePickerPlugin } from "./grapesPlugins/variablePicker";
import { MailingMeta } from "./MailingMeta";
import { TemplatePicker } from "./TemplatePicker";
import { DEFAULT_TEMPLATE, type MailingTemplateMeta } from "./templates";
import { createTemplate } from "../api/templates";

const PREHEADER_OPEN = "<!--tvs-preheader-->";
const PREHEADER_CLOSE = "<!--/tvs-preheader-->";

function injectPreheader(html: string, preheader: string): string {
  const block = `${PREHEADER_OPEN}<div style="display:none; font-size:1px; line-height:1px; max-height:0px; max-width:0px; opacity:0; overflow:hidden; mso-hide:all; font-family:sans-serif;">${escapeHtml(preheader)}</div>${PREHEADER_CLOSE}`;
  // Strip any previous preheader block we injected to avoid duplicates
  const cleaned = html.replace(
    new RegExp(`${PREHEADER_OPEN}[\\s\\S]*?${PREHEADER_CLOSE}`, "g"),
    "",
  );
  return cleaned.replace(/<body([^>]*)>/i, `<body$1>${block}`);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

interface Props {
  onLogout: () => void;
}

export function Editor({ onLogout }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<GrapesEditor | null>(null);

  const [name, setName] = useState<string>(`tvs_template_${new Date().toISOString().slice(0, 10)}`);
  const [subject, setSubject] = useState<string>(DEFAULT_TEMPLATE.defaultSubject);
  const [preheader, setPreheader] = useState<string>(DEFAULT_TEMPLATE.defaultPreheader);
  const [pickerOpen, setPickerOpen] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Auto-dismiss the save toast after a few seconds
  useEffect(() => {
    if (!savedId && !error) return;
    const ms = error ? 8000 : 4000;
    const t = window.setTimeout(() => {
      setSavedId(null);
      setError(null);
    }, ms);
    return () => window.clearTimeout(t);
  }, [savedId, error]);

  // Mount GrapesJS once
  useEffect(() => {
    if (!containerRef.current || editorRef.current) return;

    const editor = grapesjs.init({
      container: containerRef.current,
      fromElement: false,
      height: "100%",
      width: "auto",
      storageManager: false,
      plugins: [presetNewsletter, tvsBlocksPlugin, variablePickerPlugin],
      pluginsOpts: {
        // @ts-expect-error preset accepts a plain options object
        [presetNewsletter]: {
          modalLabelImport: "Pega tu HTML",
          modalLabelExport: "Copia el HTML",
          codeViewerTheme: "material",
          importPlaceholder:
            "<table>...</table><!-- pega un mailing existente para importarlo -->",
          inlineCss: true,
        },
      },
      canvas: {
        styles: ["https://fonts.googleapis.com/css2?family=Inter:wght@400;600&display=swap"],
      },
    });

    editorRef.current = editor;

    return () => {
      editor.destroy();
      editorRef.current = null;
    };
  }, []);

  const applyTemplate = useCallback((tpl: MailingTemplateMeta) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.setComponents(tpl.html);
    if (tpl.defaultSubject) setSubject(tpl.defaultSubject);
    if (tpl.defaultPreheader) setPreheader(tpl.defaultPreheader);
    setPickerOpen(false);
  }, []);

  // Apply default template once editor is ready
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const onLoad = () => {
      editor.setComponents(DEFAULT_TEMPLATE.html);
    };
    editor.on("load", onLoad);
    return () => {
      editor.off("load", onLoad);
    };
  }, []);

  const onMetaChange = useCallback(
    (patch: Partial<{ name: string; subject: string; preheader: string }>) => {
      if (patch.name !== undefined) setName(patch.name);
      if (patch.subject !== undefined) setSubject(patch.subject);
      if (patch.preheader !== undefined) setPreheader(patch.preheader);
    },
    [],
  );

  const onSave = useCallback(async () => {
    const editor = editorRef.current;
    if (!editor) return;
    setSaving(true);
    setError(null);
    setSavedId(null);
    try {
      // Run the preset's CSS-inlining command (uses juice)
      const inlined = (editor.runCommand("gjs-get-inlined-html") as string) ?? "";
      const html = injectPreheader(inlined || editor.getHtml(), preheader);
      const r = await createTemplate(name.trim(), html, subject.trim(), preheader.trim() || null);
      setSavedId(r.template_id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [name, subject, preheader]);

  return (
    <div className="flex h-screen flex-col bg-slate-50">
      <MailingMeta
        name={name}
        subject={subject}
        preheader={preheader}
        saving={saving}
        savedId={savedId}
        error={error}
        onChange={onMetaChange}
        onOpenTemplates={() => setPickerOpen(true)}
        onSave={onSave}
        onLogout={onLogout}
        onDismissToast={() => {
          setSavedId(null);
          setError(null);
        }}
      />

      <div className="flex-1 overflow-hidden">
        <div ref={containerRef} className="h-full" />
      </div>

      <TemplatePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={applyTemplate}
      />
    </div>
  );
}
